import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ORPCError } from "@orpc/client";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { orpc } from "@/lib/orpc";

export const Route = createFileRoute("/_authenticated/m/$mentionId")({
  component: MentionOpen,
});

/** The email says little, so this resolves the mention to its record on the server by id (so old
 * mentions open as well as new), marks it read and lands the reader there. */
function MentionOpen() {
  const { mentionId } = Route.useParams();
  const navigate = useNavigate();
  const opened = useRef(false);

  const mention = useQuery({
    ...orpc.communications.mentions.open.queryOptions({ input: { mentionId } }),
    retry: false,
  });

  const destination = mention.data?.path ?? null;

  useEffect(() => {
    if (!destination || opened.current) return;
    opened.current = true;
    void navigate({ href: destination, replace: true });
  }, [destination, navigate]);

  if (mention.isPending || destination) {
    return (
      <div
        aria-live="polite"
        className="text-muted-foreground flex h-64 items-center justify-center gap-2"
      >
        <h1 className="sr-only">Opening mention</h1>
        <Spinner />
        Opening…
      </div>
    );
  }

  /* A mention that is gone is a different answer from a read that failed:
     "it may have been removed" after a dropped connection sends people
     looking for something that is still there. */
  const gone =
    mention.error instanceof ORPCError &&
    (mention.error.code === "NOT_FOUND" || mention.error.code === "FORBIDDEN");
  return (
    <Empty className="py-16">
      <EmptyHeader>
        <h1 className="text-lg font-medium tracking-tight text-balance">
          {gone ? "That mention is not available" : "Couldn't open that mention"}
        </h1>
        <EmptyDescription>
          {gone
            ? "It may have been removed, or it was addressed to a different account. Your own mentions are all in one place."
            : "Check your connection and try again, or find it with your other mentions."}
        </EmptyDescription>
      </EmptyHeader>
      <div className="flex flex-wrap justify-center gap-2">
        {gone ? null : (
          <Button
            type="button"
            variant="outline"
            disabled={mention.isRefetching}
            onClick={() => void mention.refetch()}
          >
            {mention.isRefetching ? "Trying again…" : "Try again"}
          </Button>
        )}
        <Button asChild>
          <Link to="/mentions" replace>
            Go to mentions
          </Link>
        </Button>
      </div>
    </Empty>
  );
}
